<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 550px)' : 560"
    persistent
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Receipt settings</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          @click="showPreview = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="receipt-dialog-layout">
          <div class="receipt-form-pane">
            <div class="pa-6">
              <v-select
                v-model="localForm.paymentMethod"
                :items="paymentMethodItems"
                label="Payment method *"
                outlined
                dense
                :rules="paymentMethodRules"
              />

              <v-alert
                v-if="hasMixedItemVat"
                dense
                outlined
                type="info"
                class="mb-4"
              >
                Mixed item VAT: {{ itemVatSummary }}. The receipt uses the VAT rate stored on each Sales Order item.
              </v-alert>

              <v-combobox
                v-else
                v-model="localForm.vat"
                :items="vatItems"
                label="Fallback VAT key *"
                outlined
                dense
                clearable
                :rules="vatRules"
                @change="normalizeVat"
              />

              <v-text-field
                v-model="localForm.prefix"
                label="Prefix"
                outlined
                dense
                hint="Default: VKNY"
                persistent-hint
              />

              <template v-if="isForeignCurrency">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="localForm.exchangeBank"
                      label="Exchange bank"
                      outlined
                      dense
                      hint="Pl. MNB"
                      persistent-hint
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="localForm.exchangeRate"
                      label="Exchange rate"
                      type="number"
                      min="0"
                      step="0.0001"
                      outlined
                      dense
                      :rules="exchangeRateRules"
                    />
                  </v-col>
                </v-row>
              </template>

              <v-textarea
                v-model="localForm.note"
                label="Note"
                outlined
                dense
                auto-grow
                rows="3"
                hint="Optional note that will appear on the receipt"
                persistent-hint
              />
            </div>
          </div>

          <v-expand-x-transition>
            <div v-if="showPreview" class="receipt-preview-pane">
              <v-divider vertical />

              <div class="pa-6 preview-pane-inner">
                <InvoicePreviewPanel
                  document-type="receipt"
                  :order="invoiceDatas"
                  :settings="previewSettings"
                  :seller="seller"
                  :currency="currencyLabel"
                />
              </div>
            </div>
          </v-expand-x-transition>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />

        <v-btn
          v-if="invoiceDatas"
          color="primary"
          class="mr-2"
          @click="openPreview"
          :disabled="loading"
        >
          {{ !showPreview ? 'Receipt preview' : 'Close preview' }}
        </v-btn>

        <v-btn text @click="closeDialog"
          :disabled="loading"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InvoicePreviewPanel from './InvoicePreviewPanel.vue'
import Swal from "sweetalert2";

export default {
  name: 'ReceiptDialog',

  components: {
    InvoicePreviewPanel,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({
        paymentMethod: null,
        vat: null,
        note: null,
        prefix: 'VKNY',
        exchangeBank: 'MNB',
        exchangeRate: null,
      }),
    },
    invoiceDatas: {
      type: Object,
      default: () => null,
    },
    seller: {
      type: Object,
      default: () => ({
        name: 'Kismetál Kft.',
        address: null,
        taxNumber: null,
        email: null,
        phone: null,
        bankAccount: null,
      }),
    },
  },

  data() {
    return {
      showPreview: false,
      vatDefaultApplied: false,
      datas: null,
      localForm: {
        paymentMethod: null,
        vat: null,
        note: null,
        prefix: 'VKNY',
        exchangeBank: 'MNB',
        exchangeRate: null,
      },
      paymentMethodItems: [
        'Készpénz',
        'Átutalás',
        'Bankkártya',
      ],
      vatItems: [
        'TAHK', 'TAM', 'AAM', 'EUT', 'EUKT', 'F.AFA', 'K.AFA', 'HO', 'EUE',
        'EUFADE', 'EUFAD37', 'ATK', 'NAM', 'EAM', 'KBAUK', 'KBAET',
        '0', '1', '2', '2.1', '3', '4', '4.8', '5', '5.5', '6', '7', '7.7',
        '8', '8.1', '9', '9.5', '10', '11', '12', '13', '13.5', '14', '15',
        '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '25.5',
        '26', '27',
      ],
    }
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    itemVatCodes() {
      return this.collectItemVatCodes()
    },

    hasMixedItemVat() {
      return this.itemVatCodes.length > 1
    },

    itemVatSummary() {
      return this.itemVatCodes.join(', ')
    },

    currencyLabel() {
      const currency = this.datas?.priceList?.currency_id || 'HUF'
      return currency === 'HUF' ? 'Ft' : currency
    },

    isForeignCurrency() {
      return !['HUF', 'FT', 'Ft'].includes(String(this.currencyLabel).toUpperCase())
    },

    isValid() {
      if (!this.localForm.paymentMethod) return false
      if (!this.isValidVat(this.localForm.vat)) return false

      if (this.isForeignCurrency) {
        return (
          !!this.localForm.exchangeBank &&
          this.localForm.exchangeRate !== null &&
          this.localForm.exchangeRate !== '' &&
          Number(this.localForm.exchangeRate) > 0
        )
      }

      return true
    },

    previewSettings() {
      return {
        order_id: this.orderId,
        paymentMethod: this.localForm.paymentMethod,
        tax: this.hasMixedItemVat ? null : (this.localForm.vat || null),
        note: this.localForm.note || null,
        prefix: this.localForm.prefix || 'VKNY',
        exchangeBank: this.isForeignCurrency ? this.localForm.exchangeBank || 'MNB' : null,
        exchangeRate: this.isForeignCurrency && this.localForm.exchangeRate !== null
          ? Number(this.localForm.exchangeRate)
          : null,
      }
    },

    paymentMethodRules() {
      return [
        v => !!v || 'You must select a payment method',
      ]
    },

    vatRules() {
      return [
        v => !!v || 'You must select a VAT rate',
        v => this.vatItems.includes(String(v)) || 'Only values from the list can be selected',
      ]
    },

    exchangeRateRules() {
      return [
        () => !this.isForeignCurrency || !!this.localForm.exchangeBank || 'You must select an exchange bank',
        v => !this.isForeignCurrency || (v !== null && v !== '') || 'You must enter an exchange rate',
        v => !this.isForeignCurrency || !isNaN(Number(v)) || 'The exchange rate must be a number',
        v => !this.isForeignCurrency || Number(v) > 0 || 'The exchange rate must be a positive number',
      ]
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.syncForm()
        } else {
          this.showPreview = false
        }
      },
    },

    initialData: {
      deep: true,
      handler() {
        if (this.value) {
          this.syncForm()
        }
      },
    },

    invoiceDatas: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.datas = val
          this.calculateFAFA()
          this.setInitialTax()
        }
      },
    },

    'localForm.vat'(newVat) {
      if (String(newVat).includes('F.AFA')) {
        const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'
        if (!this.localForm.note || !this.localForm.note.includes(text)) {
          this.localForm.note = this.localForm.note
            ? this.localForm.note + '\n' + text
            : text
        }
      }
    },
  },

  methods: {
    normalizeItemVat(value) {
      if (value === null || value === undefined || value === '') return null

      const normalized = String(value).trim().toUpperCase().replace(',', '.')
      return this.vatItems.includes(normalized) ? normalized : null
    },

    normalizeBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    normalizeSalesPointIdentifier(value) {
      if (value === null || value === undefined || typeof value === 'object') return ''

      return String(value)
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/[^a-z0-9]/g, '')
    },

    isKismetalVaskerSalesPoint() {
      const salesPoint = this.invoiceDatas?.salesPoint || this.invoiceDatas?.sales_point || {}
      const candidates = [
        salesPoint,
        salesPoint?.name,
        salesPoint?.slug,
        salesPoint?.domain,
        salesPoint?.website,
        salesPoint?.website?.domain,
        salesPoint?.site?.domain,
        this.invoiceDatas?.sales_point,
        this.invoiceDatas?.sales_point_name,
        this.invoiceDatas?.salesPointName,
      ]

      return candidates.some(value => this.normalizeSalesPointIdentifier(value).includes('kismetalvasker'))
    },

    isEurOrder() {
      const orderCurrency = String(
        this.invoiceDatas?.priceList?.currency_id
        || this.invoiceDatas?.price_list?.currency_id
        || this.invoiceDatas?.currency_id
        || ''
      ).trim().toUpperCase()

      if (orderCurrency === 'EUR') return true

      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      const itemCurrencies = items
        .map(item => String(
          item?.currency_id
          || item?.currency?.id
          || item?.priceList?.currency_id
          || item?.price_list?.currency_id
          || ''
        ).trim().toUpperCase())
        .filter(Boolean)

      return itemCurrencies.length > 0 && itemCurrencies.every(currency => currency === 'EUR')
    },

    resolveAutomaticTaxDefault() {
      if (this.isEurOrder()) return 'EUT'

      if (
        this.isKismetalVaskerSalesPoint()
        && this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      ) {
        return 'F.AFA'
      }

      return null
    },

    resolveItemVat(item) {
      const automaticTax = this.resolveAutomaticTaxDefault()
      const zeroTax = this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      const reverseTax = this.normalizeBoolean(this.invoiceDatas?.reverse_tax)
      const isService = this.normalizeBoolean(item?.service) || this.normalizeBoolean(item?.item?.service)

      if (automaticTax) return automaticTax

      if (zeroTax) {
        const storedVatName = this.normalizeItemVat(item?.vat_name)

        if (storedVatName && Number.isNaN(Number(storedVatName))) {
          return storedVatName
        }

        return '0'
      }

      if (reverseTax && !isService) return 'F.AFA'

      return this.normalizeItemVat(item?.vat_name)
        || this.normalizeItemVat(item?.vat_value)
        || this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || '27'
    },

    collectItemVatCodes() {
      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      return [...new Set(items.map(item => this.resolveItemVat(item)).filter(Boolean))]
    },

    normalizeVat() {
      if (this.localForm.vat === null || this.localForm.vat === '') {
        this.localForm.vat = null
        return
      }

      this.localForm.vat = String(this.localForm.vat).trim().toUpperCase()
    },

    isValidVat(vat) {
      if (vat === null || vat === '') return true
      const normalized = String(vat).trim().toUpperCase()
      return this.vatItems.includes(normalized)
    },

    syncForm() {
      this.vatDefaultApplied = false

      this.localForm = {
        paymentMethod: this.initialData?.paymentMethod || 'Készpénz',
        vat: this.initialData?.vat ?? '27',
        note: this.initialData?.note || null,
        prefix: this.initialData?.prefix || 'VKNY',
        exchangeBank: this.initialData?.exchangeBank || 'MNB',
        exchangeRate: this.initialData?.exchangeRate ?? null,
      }

      this.normalizeVat()
      this.calculateFAFA()
      this.setInitialTax()
    },

    openPreview() {
      if (this.showPreview) {
        this.showPreview = false
        return
      }

      this.normalizeVat()

      if (!this.isValid) {
        return
      }

      this.showPreview = true
    },

    closeDialog() {
      this.showPreview = false
      this.syncForm()
      this.dialogProxy = false
    },

    submit() {
      this.normalizeVat()

      if (!this.isValid) {
        return
      }

      const payload = {
        order_id: this.orderId,
        paymentMethod: this.localForm.paymentMethod,
        tax: this.hasMixedItemVat ? null : (this.localForm.vat || null),
        note: this.localForm.note || null,
        prefix: this.localForm.prefix || 'VKNY',
        exchangeBank: this.isForeignCurrency ? this.localForm.exchangeBank || 'MNB' : null,
        exchangeRate: this.isForeignCurrency && this.localForm.exchangeRate !== null
          ? Number(this.localForm.exchangeRate)
          : null,
      }

      Swal.fire({
        title: '<strong>Warning</strong>',
        icon: 'warning',
        width: '600px',
        text: 'Are you sure you want to generate the receipt? This action cannot be undone.',
        showCloseButton: true,
        focusConfirm: false,
        confirmButtonText: 'Yes, generate receipt',
        confirmButtonColor: '#00c300',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$emit('save', payload)
          //this.dialogProxy = false
        }
      })
    },

    calculateFAFA() {
      if (!this.invoiceDatas) return

      const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'

      if (this.itemVatCodes.includes('F.AFA') && (!this.localForm.note || !this.localForm.note.includes(text))) {
        this.localForm.note = this.localForm.note
          ? this.localForm.note + '\n' + text
          : text
      }
    },

    setInitialTax() {
      if (!this.invoiceDatas || this.vatDefaultApplied) return

      if (this.hasMixedItemVat) {
        this.localForm.vat = null
        this.vatDefaultApplied = true
        return
      }

      const automaticTax = this.resolveAutomaticTaxDefault()

      this.localForm.vat = automaticTax
        || this.itemVatCodes[0]
        || this.normalizeItemVat(this.localForm.vat)
        || this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || '27'

      this.vatDefaultApplied = true
    },
  },
}
</script>

<style scoped>
.receipt-dialog-layout {
  display: flex;
  align-items: stretch;
  min-height: 520px;
}

.receipt-form-pane {
  flex: 0 0 560px;
  max-width: 560px;
}

.receipt-preview-pane {
  display: flex;
  flex: 1;
  min-width: 0;
  background: #fafafa;
}

.preview-pane-inner {
  flex: 1;
  min-width: 0;
  overflow: auto;
}
</style>